import { useMemo } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import type { NavItem } from "@impero-com/ui";
import { groupedNavConfig, resolveNavConfig } from "@impero-com/ui/domain";

import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";

import { navIconNodes } from "./navIcons";
import { buildFlatNavTree } from "./navTree";
import { usePendingActionsCountQuery } from "./queries/usePendingActionsCountQuery";
import type { ResolvedNavItem } from "./resolveNavItems";
import { resolveNavItems } from "./resolveNavItems";
import { useNewMenu } from "./useNewMenu";

interface NavMenu {
    readonly flat: readonly ResolvedNavItem[];
    readonly grouped: readonly NavItem[];
}

export function useNavMenu(): NavMenu {
    const { t } = useTranslation();
    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const imperoShareEnabled = useFeatureFlag("imperoShare");
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const newMenu = useNewMenu();
    const { data: pendingActionsCount } = usePendingActionsCountQuery(newMenu);

    return useMemo(() => {
        const flags = {
            controlTesting: controlTestingEnabled,
            imperoShare: imperoShareEnabled,
            controlEntities: controlEntitiesEnabled,
        };

        return {
            flat: resolveNavItems(buildFlatNavTree(t), flags),
            grouped: resolveNavConfig(groupedNavConfig, {
                t,
                icons: navIconNodes,
                flags,
                counts: {
                    pendingActions:
                        pendingActionsCount === 0
                            ? undefined
                            : pendingActionsCount,
                },
            }),
        };
    }, [
        t,
        controlTestingEnabled,
        imperoShareEnabled,
        controlEntitiesEnabled,
        pendingActionsCount,
    ]);
}
